/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableProps } from "../../../interfaces/table";
import { useTimeStore } from "../../../stores/time";
import useModals from "../../../hooks/useModals";
import Time from "../../time/Time";
import DeleteTime from "../../time/DeleteTime";
import { useEffect, useRef } from "react";
import { useUtilsStore } from "../../../stores/components/utils";
import {
  Badge,
  Checkbox,
  DesktopTable,
  IconButton,
  MicroLabel,
  MobileCardFooter,
  MobileCardList,
  MobileField,
  MobileFieldGrid,
  TableEmptyState,
  TableFrame,
  Td,
  Th,
  TrashIcon,
  cardClasses,
  emptyStateIcon,
  renderCellValue,
  rowClasses,
} from "./tableUi";

const TableTime = ({ columns, data }: TableProps) => {
  const { idTimesSelected, setIdTimesSelected, setIdTimeSelected } =
    useTimeStore();
  const { searchTerm } = useUtilsStore();

  // Ajout de la ref pour le conteneur du tableau
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  // Fonction pour gérer le clic sur une ligne
  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement | HTMLDivElement>,
    id: number
  ) => {
    e.stopPropagation();
    e.preventDefault();

    setIdTimeSelected(id);
    openModal("time", <Time closeModal={() => closeModal("time")} />);
  };

  const handleRowClickAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.target.checked) {
      setIdTimesSelected(data.map((time) => time.id));
    } else {
      setIdTimesSelected([]);
    }
  };

  // Fonction pour gérer la checkbox sur une ligne
  const handleRowClickCheck = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    if (e.target.checked) {
      setIdTimesSelected((prev) => [...prev, id]);
    } else {
      setIdTimesSelected((prev) => prev.filter((time) => time !== id));
    }
  };

  // Fonction pour afficher la valeur de la cellule en fonction de la colonne et de la ligne pour les temps administratifs
  const getDisplayValue = (col: any, row: any) => {
    if (col.accessor === "customerFile" && row[col.accessor] === null) {
      return "Administratif";
    }
    if (col.accessor === "mission" && row[col.accessor] === null) {
      return row.timesLabel;
    }
    return row[col.accessor];
  };

  useEffect(() => {
    if (searchTerm !== "" && tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  const [primaryColumn, ...secondaryColumns] = columns;

  // Les temps administratifs et les motifs d'absence sont mis en évidence
  const renderCell = (col: any, row: any) => {
    if (col.accessor === "customerFile" && row[col.accessor] === null) {
      return <Badge tone="amber">Administratif</Badge>;
    }
    if (col.accessor === "mission" && row[col.accessor] === null) {
      return <span className="text-slate-500 italic">{row.timesLabel}</span>;
    }
    return renderCellValue(getDisplayValue(col, row));
  };

  const renderDeleteButton = (id: number) => (
    <IconButton
      label="Supprimer"
      tone="danger"
      onClick={(e) => {
        e.stopPropagation();
        setIdTimeSelected(id);
        openModal(
          "deleteTime",
          <DeleteTime closeModal={() => closeModal("deleteTime")} />
        );
      }}
    >
      <TrashIcon />
    </IconButton>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null
      )}

      <TableFrame scrollRef={tableContainerRef}>
        {data.length === 0 ? (
          <TableEmptyState
            title="Aucun temps trouvé"
            description="Les temps saisis apparaîtront ici."
            icon={emptyStateIcon(
              "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            )}
          />
        ) : (
          <>
            {/* Vue tableau — écrans larges */}
            <DesktopTable>
              <thead className="sticky top-0 z-10">
                <tr>
                  <Th className="w-px">
                    <Checkbox
                      label="Tout sélectionner"
                      checked={
                        idTimesSelected.length === data.length && data.length > 0
                      }
                      onChange={(e) => handleRowClickAll(e)}
                    />
                  </Th>
                  {columns.map((col, index) => (
                    <Th key={index}>{col.header}</Th>
                  ))}
                  <Th align="right" className="w-px">
                    Actions
                  </Th>
                </tr>
              </thead>
              <tbody className="font-Nunito">
                {data.map((row, rowIndex) => {
                  const isSelected = idTimesSelected.includes(row.id);

                  return (
                    <tr
                      key={rowIndex}
                      className={rowClasses({ selected: isSelected })}
                      onClick={(e) => handleRowClick(e, row.id)}
                    >
                      <Td>
                        <Checkbox
                          label="Sélectionner la ligne"
                          checked={isSelected}
                          onChange={(e) => handleRowClickCheck(e, row.id)}
                        />
                      </Td>
                      {columns.map((col, colIndex) => (
                        <Td
                          key={colIndex}
                          className={
                            colIndex === 0
                              ? "font-semibold text-slate-900"
                              : "text-slate-600"
                          }
                        >
                          {renderCell(col, row)}
                        </Td>
                      ))}
                      <Td>
                        <div className="flex justify-end">
                          {renderDeleteButton(row.id)}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </DesktopTable>

            {/* Vue cartes — mobile et tablette */}
            <MobileCardList>
              {data.map((row, rowIndex) => {
                const isSelected = idTimesSelected.includes(row.id);

                return (
                  <li key={rowIndex}>
                    <div
                      onClick={(e) => handleRowClick(e, row.id)}
                      className={cardClasses({ selected: isSelected })}
                    >
                      <div className="flex items-start gap-3">
                        <span className="pt-1">
                          <Checkbox
                            label="Sélectionner la ligne"
                            checked={isSelected}
                            onChange={(e) => handleRowClickCheck(e, row.id)}
                          />
                        </span>
                        <div className="min-w-0">
                          <MicroLabel>{primaryColumn?.header}</MicroLabel>
                          <div className="mt-0.5 text-[15px] leading-snug font-bold wrap-break-word text-slate-900">
                            {primaryColumn ? renderCell(primaryColumn, row) : null}
                          </div>
                        </div>
                      </div>

                      {secondaryColumns.length > 0 && (
                        <MobileFieldGrid>
                          {secondaryColumns.map((col) => (
                            <MobileField
                              key={col.accessor}
                              label={col.header}
                              value={renderCell(col, row)}
                            />
                          ))}
                        </MobileFieldGrid>
                      )}

                      <MobileCardFooter>
                        {renderDeleteButton(row.id)}
                      </MobileCardFooter>
                    </div>
                  </li>
                );
              })}
            </MobileCardList>
          </>
        )}
      </TableFrame>
    </div>
  );
};

export default TableTime;
